import React, { useState, useMemo, useCallback, useEffect } from 'react';

import {
    Box,
    Typography,
    Alert,
    CircularProgress,
    Stack
} from '@mui/material';
import PlantsFilter, { FilterData } from './PlantsFilter';
import ChronologicalSlider from './ChronologicalSlider';
import EquipmentOrdersSunburst from './EquipmentOrdersSunburst';
import {
    EquipmentOrderData,
    TimeFilterState,
    SunburstNode,
    EquipmentOrderReport
} from './types/sunburst.types';
import { useGetStatsQuery } from '../redux/stats';
import { getFormatedStringFromDate } from '../utils/dateUtils';
import ExportExcel from '../common/ExportExcel';

/**
 * Equipment Orders Dashboard Component
 * 
 * Main dashboard that integrates:
 * - PlantsFilter for geographic filtering
 * - ChronologicalSlider for time-based navigation  
 * - EquipmentOrdersSunburst for hierarchical visualization
 * 
 * Features:
 * - Real-time filtering by region, country, and company
 * - Time-based animation showing equipment order evolution
 * - Interactive sunburst chart with drill-down capabilities
 * - Uses sample data service instead of API calls
 */
const EquipmentOrdersDashboard: React.FC = () => {
    // Get all data using the API query hook
    const { data: reportData, error, isLoading, isError } = useGetStatsQuery('Equipment_Orders', { skip: false });

    // Extract the actual equipment orders data
    const allData = useMemo((): EquipmentOrderData[] => {
        if (!reportData?.reportData) return [];
        return reportData.reportData as EquipmentOrderData[];
    }, [reportData]);

    // Calculate summary stats directly from typed data
    const summaryStats = useMemo(() => {
        if (!allData.length) return {
            totalOrders: 0,
            uniqueCompanies: 0,
            uniqueCountries: 0,
            uniqueRegions: 0,
            earliestYear: 2002,
            latestYear: 2025
        };

        const totalOrders = allData.length; // Count of records, not sum of OrderCount
        const uniqueCompanies = new Set(allData.map(item => item.Company_Name)).size;
        const uniqueCountries = new Set(allData.map(item => item.Country)).size;
        const uniqueRegions = new Set(allData.map(item => item.Region)).size;
        const years = allData.map(item => item.ManufactureYear).filter(year => year && !isNaN(year));
        const earliestYear = years.length > 0 ? Math.min(...years) : 2002;
        const latestYear = years.length > 0 ? Math.max(...years) : 2025;

        return {
            totalOrders,
            uniqueCompanies,
            uniqueCountries,
            uniqueRegions,
            earliestYear,
            latestYear
        };
    }, [allData]);

    // Filter states
    const [plantFilters, setPlantFilters] = useState<FilterData>({
        region: '',
        country: '',
        company: ''
    });

    // Time filter - initialize with calculated values
    const [timeFilter, setTimeFilter] = useState<TimeFilterState>({
        startYear: summaryStats.earliestYear,
        endYear: summaryStats.latestYear,
        currentYear: summaryStats.latestYear,
        isPlaying: false,
        playbackSpeed: 0.5
    });

    // Update time filter when data changes
    useEffect(() => {
        if (allData.length > 0) {
            setTimeFilter(prev => ({
                ...prev,
                startYear: summaryStats.earliestYear,
                endYear: summaryStats.latestYear,
                currentYear: prev.currentYear || summaryStats.latestYear
            }));
        }
    }, [summaryStats, allData.length]);

    // Filtered data by location
    const [filteredByLocation, setFilteredByLocation] = useState<EquipmentOrderData[]>([]);

    // Use allData as filteredByLocation when it's available
    useEffect(() => {
        if (allData.length > 0) {
            setFilteredByLocation(allData);
        }
    }, [allData]);

    // Apply time-based filtering
    const timeFilteredData = useMemo((): EquipmentOrderData[] => {
        if (!filteredByLocation.length) return [];

        return filteredByLocation.filter(item => {
            return item.ManufactureYear <= timeFilter.currentYear;
        });
    }, [filteredByLocation, timeFilter.currentYear]);

    // Handle filter changes
    const handlePlantFiltersChange = useCallback((newFilters: FilterData): void => {
        setPlantFilters(newFilters);
    }, []);

    const handleFilteredDataChange = useCallback((data: EquipmentOrderData[]): void => {
        setFilteredByLocation(data);
    }, []);

    // Time control handlers
    const handleYearChange = useCallback((year: number): void => {
        setTimeFilter(prev => ({
            ...prev,
            currentYear: year
        }));
    }, []);

    const handlePlayToggle = useCallback((): void => {
        setTimeFilter(prev => ({
            ...prev,
            isPlaying: !prev.isPlaying
        }));
    }, []);

    const handleSpeedChange = useCallback((speed: number): void => {
        setTimeFilter(prev => ({
            ...prev,
            playbackSpeed: speed
        }));
    }, []);

    // Sunburst interaction handlers
    const handleNodeClick = useCallback((node: SunburstNode): void => {
        // Node click handler - could implement drilling down or showing detailed data
    }, []);

    const handleNodeHover = useCallback((node: SunburstNode | null): void => {
        // Handle hover events if needed
        // This could be used to highlight related data in other components
    }, []);

    return (
        <Stack direction="column" sx={{ paddingY: 4, paddingX: 0, marginX: 0 }}>
            {/* Page Header */}

            {/* Loading State */}
            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress size={60} />
                    <Typography variant="body1" sx={{ ml: 2 }}>
                        Loading equipment orders data...
                    </Typography>
                </Box>
            )}

            {/* Error State */}
            {isError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    <Typography variant="h6">Error Loading Data</Typography>
                    <Typography variant="body2">
                        Failed to load equipment orders data. Please check your connection and try again.
                        {error && typeof error === 'object' && 'message' in error && (
                            <><br />Error: {String(error.message)}</>
                        )}
                    </Typography>
                </Alert>
            )}

            {/* Main Content - Show when data is available */}
            {!isLoading && !isError && allData.length > 0 && (
                <>
                    <Stack direction="row" sx={{
                        justifyContent: 'space-between', alignItems: 'center', mb: 1, marginX: '1em'
                    }}>
                        <Typography variant="body2" fontSize="75%" sx={{ mb: 2 }}>
                            This chart visualizes equipment deliveries over time. It is composed by:
                            <ul>
                                <li><b>Series</b>: Vector, Versalis, Focus, Virga, TopSpin, Digits</li>
                                <li><b>Markets</b>: Auto, Fashion, Furniture, TechTex, Lingerie, Denim</li>
                                <li><b>Products</b>: Cutters models as IX9, Q80, IP6, etc.</li>
                            </ul>
                            <p><b>Click on bubbles</b> for detailed analysis. Apply filters to restrict view.</p>
                        </Typography>
                        <Typography variant="body2" fontSize="75%" sx={{ mb: 2 }}>
                            Last updated: <b>{getFormatedStringFromDate((reportData as EquipmentOrderReport).createdAt)}</b><br />
                            <br />
                            <b>Data, chart and analysis are corrected over time.</b>
                        </Typography>
                        {(timeFilteredData?.length ?? 0) > 0 && (
                                <ExportExcel label={`Equipment_Deliveries_All`}
                            total={timeFilteredData.length} results={timeFilteredData}
                            svg={{ width: '2rem', fill: '#0E796E' }} sx={{ mb: 2 }}
                            title={`Export ${timeFilteredData.length} 'Equipment_Deliveries' results`} />
                        )}
                        
                    </Stack>

                    {/* Main Visualization */}
                    <Stack direction="column" alignItems="center" justifyContent="center" >
                        {/* Plant Filters */}
                        <PlantsFilter sx={{ borderTop: '1px solid #ccc', borderBottom: '0px solid #ccc', paddingBottom: 0, marginBottom: 0 }}
                            data={allData}
                            filters={plantFilters}
                            onFiltersChange={handlePlantFiltersChange}
                            onFilteredDataChange={handleFilteredDataChange}
                            dataCountLabel="Equipment sales"
                        />

                        {/* Chronological Slider */}
                        <ChronologicalSlider sx={{ margin: '1em', width: '90%' }}
                            yearRange={{ min: timeFilter.startYear, max: timeFilter.endYear }}
                            currentYear={timeFilter.currentYear}
                            isPlaying={timeFilter.isPlaying}
                            playbackSpeed={timeFilter.playbackSpeed}
                            onYearChange={handleYearChange}
                            onPlayToggle={handlePlayToggle}
                            onSpeedChange={handleSpeedChange}
                        />
                        <EquipmentOrdersSunburst sx={{ marginX: '-40px' }}
                            data={allData}
                            filteredData={timeFilteredData}
                            currentYear={timeFilter.currentYear}
                            width={800}
                            height={800}
                            filters={plantFilters}
                            onNodeClick={handleNodeClick}
                            onNodeHover={handleNodeHover}
                        />
                    </Stack>



                    {/* Summary Statistics 
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="h4" color="primary">
                                    {currentViewStats.totalOrders.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Total Orders
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="h4" color="secondary">
                                    {currentViewStats.uniqueCompanies}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Companies
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="h4" color="info.main">
                                    {currentViewStats.uniqueCountries}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Countries
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2, textAlign: 'center' }}>
                                <Typography variant="h4" color="success.main">
                                    {currentViewStats.uniqueRegions}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Regions
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                    */}
                </>
            )}

            {/* No Data State */}
            {!isLoading && !isError && allData.length === 0 && (
                <Alert severity="info" sx={{ mt: 3 }}>
                    <Typography variant="h6">No Data Available</Typography>
                    <Typography variant="body2">
                        {reportData ? 'No equipment sales found in the report data.' : 'No equipment sales data returned from API.'}
                    </Typography>
                </Alert>
            )}
        </Stack>
    );
};

export default EquipmentOrdersDashboard;